import Signature from './AbstractSignature';
import { CreateElement, VNode } from 'vue';
import { ChildParams, MethodAttributes, referencesMethod } from '@/templates/References';

export default function CsharpSignatureFactory(kind: string) {
  switch (kind) {
    case 'method':
      return CsharpMethodSignature;
    case 'prop':
      return CsharpPropSignature;
    case 'events':
      return CsharpEventSignature;
    case 'typedef':
      return CsharpTypeDefSignature;
    default:
      return {
        getSignature(_method: any): null {
          return null;
        },
      };
  }
}

class CsharpEventSignature extends Signature {
  public static LANG = 'csharp';

  public static getSignature(event: referencesMethod): (h: CreateElement) => VNode {
    const returnsType = this.returnsType(event.returns);
    return (h) =>
      h('code', [
        h('span', { class: 'token type md-parse' }, 'event '),
        h('span', { class: 'token punctuation' }, 'SdkEventHandler'),
        h('span', { class: 'token type md-parse' }, '<'),
        returnsType(h),
        h('span', { class: 'token type md-parse' }, '>'),
        h('span', { class: 'token function' }, ` ${event.title} `),
      ]);
  }
}

class CsharpMethodSignature extends Signature {
  public static LANG = 'csharp';

  public static getSignature(method: referencesMethod): (h: CreateElement) => VNode {
    const returnsType = this.returnsType(method.returns);
    const paramSignatures = CsharpHelper.paramSignatures(method.params);
    return (h) =>
      h('code', [
        returnsType(h),
        h('span', { class: 'token function' }, ` ${method.title}`),
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ')'),
      ]);
  }
}

class CsharpTypeDefSignature extends Signature {
  public static getSignature(typedef: referencesMethod): (h: CreateElement) => VNode {
    return CsharpHelper.getSignature(typedef, true);
  }
}

class CsharpHelper extends Signature {
  public static getSignature(
    method: referencesMethod,
    isTypeDef?: boolean
  ): (h: CreateElement) => VNode {
    const name = method.title.replace(/<.*/, '');
    const returnsType = this.returnsType(method.returns);
    const paramSignatures = this.paramSignatures(method.params);
    const paramsTypes = this.paramSignatures(method.params, true);

    return (h) =>
      h('code', [
        isTypeDef ? h('span', { class: 'token punctuation' }, 'delegate ') : null,
        returnsType(h),
        ` ${name}`,
        h('span', { class: 'token punctuation' }, '<'),
        paramsTypes(h),
        h('span', { class: 'token punctuation' }, '>'),
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ')'),
      ]);
  }

  public static paramSignatures(
    paramSignatures: ChildParams[],
    isTypeParams = false
  ): (h: CreateElement) => VNode {
    if (!paramSignatures) return (h) => h();

    return (h) =>
      h(
        'span',
        paramSignatures.map((val: ChildParams, idx: number) => {
          return h('span', [
            !isTypeParams ? null : 'in ',
            val.types ? this.returnsType(val.types)(h) : '',
            !isTypeParams ? ` ${val.title}` : null,
            !(idx === paramSignatures.length - 1)
              ? h('span', { class: 'token punctuation' }, ', ')
              : '',
          ]);
        })
      );
  }
}

class CsharpPropSignature extends Signature {
  public static LANG = 'csharp';

  public static getSignature(property: any): (h: CreateElement) => VNode {
    const returnsType = this.returnsType(property.returns);
    const returnAttributes = this.getAttributes(property.attributes);

    return (h) =>
      h('code', [
        returnsType(h),
        ` ${property.title}`,
        property.attributes ? returnAttributes(h) : '',
      ]);
  }

  public static getAttributes(attributes: MethodAttributes): (h: CreateElement) => VNode {
    if (!attributes || !Object.keys(attributes)) return (h) => h();

    return (h) =>
      h('span', [
        h('span', { class: 'token punctuation' }, ' { '),
        h(
          'span',
          Object.keys(attributes).map((key) => {
            return h('span', { class: 'token md-parse type' }, [
              key === 'readonly' ? h('span', attributes[key] ? 'get; ' : 'get; set; ') : null,
            ]);
          })
        ),
        h('span', { class: 'token punctuation' }, '}'),
      ]);
  }
}
